import { TimePeriodType } from '@/common/utils';
import { getCustomPeriodInterval } from '@/common/utils/chartUtils';
import { getUtcTimeFromTzTime } from '@/common/utils/time.utils';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import {
  WidgetIntervalType,
  WidgetChartType,
  WidgetTimePeriod,
  CalendarTimeRange,
} from '@/dashboard/shared/types/widgets.types';
import { storeToRefs } from 'pinia';
import { formatTimeRangeToUtc } from '@/common/components/molecules/timeController/utils';
import { TIME_RANGE_COUNT_BY_CHART_TYPE } from '@/dashboard/features/widgets/common/constants/define';
import { useGlobalTimePeriodStore } from '@/dashboard/stores/global-time-period';
import { RequestPeriod } from '../domain/apiRequest.types';

export const useRequestPeriod = () => {
  const { globalTickSetting } = storeToRefs(useDashboardViewStore());
  const { globalTimePeriod } = storeToRefs(useGlobalTimePeriodStore());

  const applyGlobalTickSetting = (interval: WidgetIntervalType) => {
    return interval === 'I5s' ? globalTickSetting.value : interval;
  };

  const retrieveTimePeriod = (
    _period:
      | TimePeriodType
      | {
          fromTime: string;
          toTime: string;
        },
  ): RequestPeriod => {
    if (typeof _period === 'string') {
      const interval = getCustomPeriodInterval(_period);
      return {
        period: _period,
        interval: applyGlobalTickSetting(interval),
      };
    }
    const { fromTime, toTime } = _period;
    const interval = getCustomPeriodInterval('', {
      fromTime: +getUtcTimeFromTzTime(fromTime),
      toTime: +getUtcTimeFromTzTime(toTime),
    });
    return {
      fromTime,
      toTime,
      interval: applyGlobalTickSetting(interval),
    };
  };

  const getRequestPeriodByCalendar = (
    calendarTimeRange: CalendarTimeRange[],
    chartType: WidgetChartType,
  ): RequestPeriod => {
    const timeRangeCount = TIME_RANGE_COUNT_BY_CHART_TYPE[chartType];
    if (timeRangeCount === 2) {
      const [baseTimeRange, comTimeRange] = calendarTimeRange;
      const { fromTimeUtc: baseFromTime, toTimeUtc: baseToTime } =
        formatTimeRangeToUtc(baseTimeRange);
      const { fromTimeUtc: comFromTime, toTimeUtc: comToTime } = formatTimeRangeToUtc(comTimeRange);
      const requestPeriod: RequestPeriod = retrieveTimePeriod({
        fromTime: baseFromTime,
        toTime: baseToTime,
      });

      if (comFromTime !== 'Invalid Date') {
        const additional = retrieveTimePeriod({
          fromTime: comFromTime,
          toTime: comToTime,
        });
        requestPeriod.additionalFromTime = additional.fromTime;
        requestPeriod.additionalToTime = additional.toTime;
        requestPeriod.additionalInterval = additional.interval;
      }

      return requestPeriod;
    }
    const { fromTimeUtc, toTimeUtc } = formatTimeRangeToUtc(calendarTimeRange[0]);

    return retrieveTimePeriod({
      fromTime: fromTimeUtc,
      toTime: toTimeUtc,
    });
  };

  const getRequestPeriod = (
    timePeriod?: WidgetTimePeriod,
    chartType?: WidgetChartType,
    calendarTimeRange?: CalendarTimeRange[],
  ): RequestPeriod => {
    if (timePeriod === 'globalTime') {
      if (globalTimePeriod.value.isPaused) {
        return retrieveTimePeriod({
          fromTime: globalTimePeriod.value.fromTimeUtc,
          toTime: globalTimePeriod.value.toTimeUtc,
        });
      }
      return retrieveTimePeriod(globalTimePeriod.value.timePeriod as TimePeriodType);
    }

    if (timePeriod === 'calendar') {
      return getRequestPeriodByCalendar(calendarTimeRange!, chartType!);
    }

    return retrieveTimePeriod(timePeriod ?? 'p5m');
  };

  return {
    retrieveTimePeriod,
    getRequestPeriod,
  };
};
